import React, { useEffect } from "react";
import { ArcButton, ArcStack, ArcText } from "@src/core/ui";
import { FormState, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  setZoneMapLocationUponCreation,
  useGetSafeLandZoneQuery,
  useUpdateSafeLandZoneMutation,
} from "@src/features/zones/redux";
import { routesConfig } from "@src/core/routes";
import { SafeLandZoneForm } from "@src/features/zones/components/zone-form/safe-land";
import { useDispatch } from "react-redux";
import { ZoneType } from "@src/core/features";
import { setMapLocation } from "@src/core/controllers";
import { VARIANT_SAFE_LAND } from "@src/features/zones/pages/zones-page/types";
import { ICreateSafeLandZoneForm } from "../create-zone/safe-land/types";

export const EditSafeLandZonePage: React.FC = () => {
  const { id } = useParams();
  const zoneId = id ? Number(id) : NaN;
  const { data: zone, isLoading } = useGetSafeLandZoneQuery(zoneId, {
    skip: !Number.isFinite(zoneId),
  });

  const defaultValues: ICreateSafeLandZoneForm = {
    zoneName: "",
    zoneDescription: "",
    latitude: "",
    longitude: "",
    address: "",
    zoneAltitude: 0,
    zoneType: ZoneType.TEMPORARY,
  };

  const {
    control,
    setValue,
    register,
    getValues,
    formState: { errors },
    handleSubmit,
    watch,
    reset,
  } = useForm<ICreateSafeLandZoneForm>({ defaultValues });

  const navigate = useNavigate();
  const [updateZone, { isLoading: isSaving }] = useUpdateSafeLandZoneMutation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!zone) return;
    reset({
      id: zone.id,
      zoneName: zone.name,
      zoneDescription: zone.description,
      latitude: String(zone.centerLatitude),
      longitude: String(zone.centerLongitude),
      address: zone.locationName,
      zoneAltitude: zone.altitudeMeters,
      zoneType: ZoneType.TEMPORARY,
    });
    dispatch(
      setZoneMapLocationUponCreation({
        id: zone.id,
        latitude: zone.centerLatitude,
        longitude: zone.centerLongitude,
        radius: 0,
        type: ZoneType.TEMPORARY,
        zoneName: zone.name,
        variant: VARIANT_SAFE_LAND,
      })
    );
    dispatch(
      setMapLocation({
        latitude: zone.centerLatitude,
        longitude: zone.centerLongitude,
        address: zone.locationName ?? "",
      })
    );
    return () => {
      dispatch(setZoneMapLocationUponCreation(null));
    };
  }, [zone, reset, dispatch]);

  const onCancel = () => {
    navigate(-1);
    dispatch(setZoneMapLocationUponCreation(null));
  };

  const onSubmit = async (data: ICreateSafeLandZoneForm) => {
    if (!zone) return;
    try {
      await updateZone({
        id: zone.id,
        name: data.zoneName,
        description: data.zoneDescription,
        centerLatitude: Number(data.latitude),
        centerLongitude: Number(data.longitude),
        locationName: data.address,
        altitudeMeters: data.zoneAltitude ?? 0,
        isActive: zone.isActive,
      }).unwrap();
      navigate(`${routesConfig.zones}?tab=${VARIANT_SAFE_LAND}`);
      dispatch(setZoneMapLocationUponCreation(null));
    } catch (error) {
      console.error("Failed to update safe-land zone:", error);
    }
  };

  if (!Number.isFinite(zoneId)) {
    return null;
  }

  if (isLoading || !zone) {
    return (
      <ArcStack height="100%" alignItems="center" justifyContent="center">
        <ArcText.h6 color="text.secondary">Loading zone…</ArcText.h6>
      </ArcStack>
    );
  }

  return (
    <ArcStack height="100%" direction="column" justifyContent="space-between">
      <ArcStack flex={1}>
        <ArcText.h4 fontWeight={500} marginTop={2}>
          EDIT SAFE-LAND ZONE
        </ArcText.h4>
        <SafeLandZoneForm
          control={control}
          setValue={setValue}
          register={register}
          getValues={getValues}
          formState={{ errors } as FormState<ICreateSafeLandZoneForm>}
          watch={watch}
        />
      </ArcStack>

      <ArcStack spacing={2} direction="row" px={1}>
        <ArcButton.ghost fullWidth size="large" onClick={onCancel}>
          Cancel
        </ArcButton.ghost>
        <ArcButton.brand
          fullWidth
          size="large"
          type="submit"
          isLoading={isSaving}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </ArcButton.brand>
      </ArcStack>
    </ArcStack>
  );
};
